import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "1. Who we are",
    body: (
      <>
        Prospectivly (“Prospectivly”, “we”, “us”, or “our”) operates the website
        at <a className="underline decoration-[#FF4500]" href="https://prospectivly.com">prospectivly.com</a> and
        provides a fully managed B2B outbound engine that books qualified
        meetings on the calendars of revenue teams. This Privacy Policy
        explains what information we collect, how we use it, and the choices
        you have.
      </>
    ),
  },
  {
    title: "2. Information we collect",
    body: (
      <>
        <p className="mb-4">When you request a demo or contact us, we collect:</p>
        <ul className="list-disc pl-6 space-y-2 text-[#9CA3AF]">
          <li>Contact details: name, work email, company name, website.</li>
          <li>Company context you choose to share (e.g. company size).</li>
          <li>
            Technical metadata when you visit the site: browser type, device,
            approximate location and pages viewed.
          </li>
        </ul>
        <p className="mt-4">
          We do not knowingly collect personal data from anyone under 18 and we
          do not sell personal information.
        </p>
      </>
    ),
  },
  {
    title: "3. How we use information",
    body: (
      <ul className="list-disc pl-6 space-y-2 text-[#9CA3AF]">
        <li>To respond to your demo request and schedule a call.</li>
        <li>To send you relevant information about Prospectivly’s services.</li>
        <li>To improve our site, content and customer experience.</li>
        <li>To comply with legal obligations.</li>
      </ul>
    ),
  },
  {
    title: "4. Sharing information",
    body: (
      <>
        We share personal data only with service providers who help us run our
        business — for example, hosting, analytics, email delivery and CRM
        platforms — and only to the extent needed for them to provide their
        service to us. We require all such providers to keep your data secure
        and confidential.
      </>
    ),
  },
  {
    title: "5. Cookies & analytics",
    body: (
      <>
        <p className="mb-4">
          We use a minimal set of cookies and analytics events to understand
          how visitors discover and use the site. When you first visit, you
          will see a cookie banner letting you choose between:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-[#9CA3AF]">
          <li>
            <span className="text-[#EDEDED]">Accept all</span> — essential
            cookies plus analytics events that help us improve the site.
          </li>
          <li>
            <span className="text-[#EDEDED]">Essential only</span> — only the
            cookies strictly required for the site to function (e.g. remembering
            your cookie preference, securing form submissions).
          </li>
        </ul>
        <p className="mt-4">
          You can clear your cookie preference at any time by clearing your
          browser storage for this site, which will show the banner again on
          your next visit. You can also disable cookies entirely in your
          browser settings; some interactive features may stop working as a
          result.
        </p>
      </>
    ),
  },
  {
    title: "6. Data retention",
    body: (
      <>
        We keep lead and contact information for as long as we have a
        legitimate business need (typically up to 36 months from your last
        interaction), unless a longer retention is required by law.
      </>
    ),
  },
  {
    title: "7. Your rights",
    body: (
      <>
        You can request access to, correction or deletion of your personal
        data, or ask us to stop contacting you, by emailing{" "}
        <a
          className="text-[#FF4500] underline"
          href="mailto:jahnvee@prospectivly.com"
        >
          jahnvee@prospectivly.com
        </a>
        . We will respond within a reasonable timeframe and in accordance with
        applicable law (including GDPR and the DPDP Act, where relevant).
      </>
    ),
  },
  {
    title: "8. Security",
    body: (
      <>
        We use industry-standard administrative, technical and physical
        safeguards to protect your information. No internet transmission is
        100% secure, but we work hard to keep your data safe.
      </>
    ),
  },
  {
    title: "9. Changes to this policy",
    body: (
      <>
        We may update this Privacy Policy from time to time. Significant
        changes will be reflected at the top of this page. Continued use of
        the site after changes are posted constitutes acceptance of the
        revised policy.
      </>
    ),
  },
  {
    title: "10. No unsolicited promotion of this website",
    body: (
      <>
        <p className="mb-4">
          Prospectivly maintains a strict published policy{" "}
          <span className="text-[#EDEDED]">
            against advertising this website through unsolicited messages
          </span>{" "}
          (including, but not limited to, spam email, unsolicited SMS, cold
          WhatsApp messages, automated DMs on social platforms, and any other
          form of bulk or non-opt-in promotion).
        </p>
        <p>
          We will never instruct, authorise or pay any third party to send
          unsolicited messages on our behalf. All outreach to prospects on
          behalf of our customers is conducted under their own brand and is
          fully compliant with applicable anti-spam and electronic
          communications laws (including CAN-SPAM, GDPR/PECR and the DPDP Act,
          where relevant).
        </p>
      </>
    ),
  },
  {
    title: "11. Contact",
    body: (
      <>
        For any questions about this Privacy Policy or our data practices,
        write to{" "}
        <a
          className="text-[#FF4500] underline"
          href="mailto:jahnvee@prospectivly.com"
        >
          jahnvee@prospectivly.com
        </a>
        .
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "December 10, 2025";

  return (
    <main
      data-testid="privacy-policy-page"
      className="min-h-screen bg-[#0B0D12] text-[#EDEDED]"
    >
      <header className="border-b border-[#2A2E3D]/60">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-[#9CA3AF] hover:text-[#FF4500] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="flex items-center gap-2">
            <div className="relative overflow-hidden h-8 w-8 flex-shrink-0">
              <img
                src="/prospectivly-logo.svg"
                alt="Prospectivly"
                className="absolute max-w-none"
                style={{
                  height: "58px",
                  width: "87px",
                  left: "-22px",
                  top: "-13px",
                }}
              />
            </div>
            <span className="font-sans not-italic uppercase text-xl tracking-tight text-[#EDEDED]">
              prospectivly<span className="text-[#FF4500]">.</span>
            </span>
          </div>
        </div>
      </header>

      <section className="max-w-[820px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-5">
          Legal
        </p>
        <h1 className="font-editorial font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
          Privacy <span className="italic">policy.</span>
        </h1>
        <p className="mt-6 text-[#9CA3AF]">
          Last updated: <span className="text-[#EDEDED]">{lastUpdated}</span>
        </p>

        <div className="mt-14 space-y-12">
          {sections.map((s) => (
            <article key={s.title} data-testid={`policy-${s.title.split(" ")[0].replace(".", "")}`}>
              <h2 className="font-editorial text-2xl sm:text-3xl text-[#EDEDED] mb-4 tracking-tight">
                {s.title}
              </h2>
              <div className="text-[#9CA3AF] text-base leading-relaxed">
                {s.body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-[#2A2E3D]/60">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#E03C00] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,69,0,0.35)]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Prospectivly
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#2A2E3D]/60 py-8">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-xs text-[#4B5563] flex flex-wrap justify-between gap-3">
          <span>© {new Date().getFullYear()} Prospectivly. All rights reserved.</span>
          <a
            href="mailto:jahnvee@prospectivly.com"
            className="hover:text-[#FF4500]"
          >
            jahnvee@prospectivly.com
          </a>
        </div>
      </footer>
    </main>
  );
}
